"use client";

import { Skeleton } from "@repo/ui";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";
import {
  AdvertisedRatesProvider,
  useAdvertisedRates,
} from "@/components/opportunities/advertised-pay";
import { OpportunityCard } from "@/components/opportunities/opportunity-card";
import { api } from "@/lib/trpc/react";

export function RecruitersOpportunityFeed({ ctaHref }: { ctaHref: string }) {
  const { data, isLoading } = api.opportunityListing.listPublished.useQuery({
    limit: 6,
  });

  // Pitched to recruiters as searches to source for, so a study whose
  // organization keeps it out of referrals has no place here.
  const opportunities = (data?.items ?? []).filter(
    (listing) => listing.referralsEnabled,
  );
  const rates = useAdvertisedRates(
    opportunities.map((listing) => listing.studyId),
  );
  const liveCount = opportunities.length;

  return (
    <section className="py-16 md:py-24">
      <Container>
        <div className="mb-12 flex flex-col items-center gap-4">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Open searches
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-[820px] text-center text-balance">
            Our live opportunities
          </h2>
          <p className="text-marketing-text-muted max-w-[560px] text-center text-base">
            A live slice of the demand board. Every search here is unfilled
            right now, across every category, and the placement goes to whoever
            sources the right person first. The full board updates daily.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 6 }).map((_, idx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`feed-skel-${idx}`}
                  className="border-border bg-card flex flex-col rounded-xl border px-3.5 py-3"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <Skeleton className="h-5 w-5 rounded-full" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                  <Skeleton className="mb-1 h-4 w-3/4" />
                  <Skeleton className="mb-1 h-3 w-full" />
                  <Skeleton className="mb-2 h-3 w-2/3" />
                  <div className="mt-auto flex items-center gap-2 pt-2">
                    <Skeleton className="h-3.5 w-20" />
                    <Skeleton className="ml-auto h-3 w-16" />
                  </div>
                </div>
              ))
            : opportunities.map((opp, index) => (
                <motion.div
                  key={opp.id}
                  initial={{ opacity: 0, transform: "translateY(16px)" }}
                  whileInView={{ opacity: 1, transform: "translateY(0px)" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                >
                  <AdvertisedRatesProvider rates={rates}>
                    <OpportunityCard
                      opportunity={opp}
                      size="compact"
                      openInNewTab
                    />
                  </AdvertisedRatesProvider>
                </motion.div>
              ))}
        </div>

        {/* Footer line + CTA */}
        <div className="mt-10 flex flex-col items-center gap-3">
          {!isLoading && liveCount > 0 && (
            <p className="text-marketing-text-muted text-copy-13">
              Plus dozens more open searches inside. Sign up to see the full
              board.
            </p>
          )}
          <Link
            href={ctaHref}
            className="bg-marketing-blue inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-colors hover:opacity-90"
          >
            Refer Now
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </section>
  );
}
